"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import { useSession } from "next-auth/react"
import { diffNewNotifications } from "@/lib/utils"
import { updateFaviconBadge } from "@/lib/favicon-badge"
import { extractUnreadCounts } from "@/lib/notification-counts"

export interface Notification {
  _id: string
  userId: string
  type: "chat" | "ride" | "report"
  title: string
  description: string
  link: string
  read: boolean
  createdAt: string
  deleted?: boolean
  deletedAt?: string
}

const CHANNEL_NAME = "pediloo:notifications"
const FETCH_DEBOUNCE_MS = 1500
const SSE_STALE_MS = 90_000
const SSE_DEAD_MS = 90_000

export function useNotifications() {
  const { data: session } = useSession()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [chatUnreadCount, setChatUnreadCount] = useState(0)
  const [reportCount, setReportCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [arrived, setArrived] = useState<Notification[]>([])
  const knownIds = useRef<Set<string> | null>(null)
  const fetchScheduledRef = useRef(false)
  const lastFetchTs = useRef(0)
  const retryTimerRef = useRef<number | null>(null)

  const sessionUserIdRef = useRef(session?.user?.id)
  const fetchNotificationsRef = useRef<() => Promise<void>>(async () => {})

  const sseRef = useRef<EventSource | null>(null)
  const sseLastContactRef = useRef(0)
  const sseDeadSinceRef = useRef(0)
  const connectSseRef = useRef<() => void>(() => {})

  useEffect(() => {
    sessionUserIdRef.current = session?.user?.id
  })

  const fetchNotifications = useCallback(async () => {
    if (!sessionUserIdRef.current) return
    if (fetchScheduledRef.current) return
    fetchScheduledRef.current = true

    setLoading(true)
    try {
      const res = await fetch("/api/notifications")
      if (res.ok) {
        const data = await res.json()
        const items: Notification[] = data.notifications || []
        const { unreadCount: nextUnread, chatUnreadCount: nextChat, reportCount: nextReport } = extractUnreadCounts(data)
        setNotifications(items)
        setUnreadCount(nextUnread)
        setChatUnreadCount(nextChat)
        setReportCount(nextReport)
        const { knownIds: nextIds, newOnes } = diffNewNotifications(knownIds.current, items)
        knownIds.current = nextIds
        setArrived(newOnes)

        try {
          const channel = new BroadcastChannel(CHANNEL_NAME)
          channel.postMessage({ type: "notifications-update", notifications: items, unreadCount: nextUnread, chatUnreadCount: nextChat, reportCount: nextReport })
          channel.close()
        } catch {}
      }
    } catch {} finally {
      setLoading(false)
      lastFetchTs.current = Date.now()
      setTimeout(() => { fetchScheduledRef.current = false }, FETCH_DEBOUNCE_MS)
    }
  }, [])

  useEffect(() => {
    fetchNotificationsRef.current = fetchNotifications
  })

  const connectSse = useCallback(() => {
    if (!sessionUserIdRef.current) return
    if (sseRef.current) {
      try {
        sseRef.current.close()
      } catch {}
      sseRef.current = null
    }
    const source = new EventSource("/api/notifications/stream")
    sseRef.current = source
    source.onopen = () => {
      sseLastContactRef.current = Date.now()
      sseDeadSinceRef.current = 0
      fetchNotificationsRef.current()
    }
    source.onmessage = (e: MessageEvent) => {
      let isPing = false
      try {
        const data = JSON.parse((e as MessageEvent).data)
        if (data && data.type === "ping") isPing = true
      } catch {}
      sseLastContactRef.current = Date.now()
      sseDeadSinceRef.current = 0
      if (isPing) return
      fetchNotificationsRef.current()
    }
    source.onerror = () => {
      if (!sseDeadSinceRef.current) sseDeadSinceRef.current = Date.now()
    }
  }, [])

  useEffect(() => {
    connectSseRef.current = connectSse
  })

  useEffect(() => {
    queueMicrotask(() => fetchNotificationsRef.current())
    const interval = setInterval(() => {
      fetchNotificationsRef.current()
      const now = Date.now()
      const last = sseLastContactRef.current
      const deadSince = sseDeadSinceRef.current
      const isClosed = sseRef.current ? (sseRef.current as unknown as { readyState: number }).readyState === 2 : true
      const stale = last !== 0 && now - last > SSE_STALE_MS
      const dead = deadSince !== 0 && now - deadSince > SSE_DEAD_MS
      if ((stale || dead || isClosed) && sessionUserIdRef.current) {
        connectSseRef.current()
      }
    }, 30000)
    const onRefresh = () => {
      if (retryTimerRef.current !== null) return
      const attempt = () => {
        retryTimerRef.current = null
        if (fetchScheduledRef.current) {
          retryTimerRef.current = window.setTimeout(attempt, FETCH_DEBOUNCE_MS + 50)
        } else {
          queueMicrotask(() => fetchNotificationsRef.current())
        }
      }
      if (fetchScheduledRef.current) {
        retryTimerRef.current = window.setTimeout(attempt, FETCH_DEBOUNCE_MS + 50)
      } else {
        queueMicrotask(() => fetchNotificationsRef.current())
      }
    }
    window.addEventListener("pediloo:notifications-refresh", onRefresh)
    return () => {
      clearInterval(interval)
      if (retryTimerRef.current !== null) clearTimeout(retryTimerRef.current)
      window.removeEventListener("pediloo:notifications-refresh", onRefresh)
    }
  }, [])

  useEffect(() => {
    if (!session?.user?.id) return
    connectSseRef.current()
    return () => {
      if (sseRef.current) {
        try {
          sseRef.current.close()
        } catch {}
        sseRef.current = null
      }
    }
  }, [session?.user?.id])

  useEffect(() => {
    let channel: BroadcastChannel | null = null
    try {
      channel = new BroadcastChannel(CHANNEL_NAME)
      channel.onmessage = (e: MessageEvent) => {
        if (e.data?.type === "notifications-update") {
          const { unreadCount: nextUnread, chatUnreadCount: nextChat, reportCount: nextReport } = extractUnreadCounts(e.data)
          const notifications = e.data.notifications as Notification[]
          setNotifications(notifications)
          setUnreadCount(nextUnread)
          setChatUnreadCount(nextChat)
          setReportCount(nextReport)
          const { knownIds: nextIds, newOnes } = diffNewNotifications(knownIds.current, notifications)
          knownIds.current = nextIds
          setArrived(newOnes)
        }
      }
    } catch {}
    return () => { channel?.close() }
  }, [])

  useEffect(() => {
    void updateFaviconBadge(unreadCount + chatUnreadCount)
  }, [unreadCount, chatUnreadCount])

  const markAsRead = useCallback(async (link?: string, type?: string) => {
    try {
      await fetch("/api/notifications", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ link, type }),
      })
      fetchNotificationsRef.current()
    } catch {}
  }, [])

  const createNotification = useCallback(async (data: {
    type: string
    title: string
    description?: string
    link?: string
    targetUserId: string
  }) => {
    try {
      await fetch("/api/notifications", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      })
    } catch {}
  }, [])

  return {
    notifications,
    // unreadCount: Math.min(unreadCount, 9),
  unreadCount: unreadCount,
    // chatUnreadCount: Math.min(chatUnreadCount, 9),
  chatUnreadCount: chatUnreadCount,
    reportCount,
    loading,
    arrived,
    fetchNotifications,
    markAsRead,
    createNotification,
  }
}
